import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import RightPanel from '../../components/RightPanel';
import LoadingCard from '../../components/LoadingCard';
// @ts-ignore
import DraggableTable from '../../components/DraggableTable';

import { useDeals } from './hooks/useDeals';
import { useDealPanel } from './hooks/useDealPanel';
import { useLightbox } from '../../hooks/useLightbox';
import { useHighlight } from '../../hooks/useHighlight';

import { DEALS_COLUMNS } from './components/dealColumns';
import DealFilters from './components/DealFilters';
import DealPanelHeader from './components/DealPanelHeader';
import DealViewPanel from './components/DealViewPanel';
import DealEditForm from './components/DealEditForm';

const DealsPage: React.FC = () => {
  const [updating, setUpdating] = useState<number | null>(null);
  const [creatingProject, setCreatingProject] = useState<number | null>(null);

  const {
    customers,
    selectedCustomer,
    setSelectedCustomer,
    stage,
    setStage,
    data,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    filteredDeals,
    load,
  } = useDeals();

  const { highlightedId, setHighlightedId } = useHighlight(data);

  const {
    selectedDeal,
    setSelectedDeal,
    viewMode,
    setViewMode,
    submitting,
    loadingDeal,
    panelError,
    setPanelError,
    customerContacts,
    loadingContacts,
    handleViewDetails,
    handleEdit,
    handleClosePanel,
    handleDealChange,
    handleStageTransition,
    handleUpdateDeal,
    navigate,
  } = useDealPanel(load);

  const { lightbox, setLightbox, openLightbox } = useLightbox();

  return (
    <div className="theme-card rounded-2xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold theme-text-primary">Deals</h2>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <DealFilters
        customers={customers}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        stage={stage}
        setStage={setStage}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onRefresh={() => load()}
      />

      {/* Table */}
      {loading ? (
        <div className="p-4">
          <LoadingCard title="Deals" message="Loading deals..." />
        </div>
      ) : (
        <DraggableTable
          columns={DEALS_COLUMNS(
            customers,
            highlightedId,
            loadingDeal,
            updating,
            setUpdating,
            creatingProject,
            setCreatingProject,
            handleViewDetails,
            handleEdit,
            load,
            setHighlightedId,
            setError,
            navigate,
          )}
          data={filteredDeals}
          emptyMessage={searchQuery ? 'No deals found matching your search' : 'No deals yet'}
        />
      )}

      {/* Right Side Panel for View Details / Edit */}
      <RightPanel
        isOpen={!!selectedDeal && !!viewMode}
        onClose={handleClosePanel}
        title={viewMode === 'view' ? 'Deal Details' : 'Edit Deal'}
        widthClass="w-full md:w-[calc(100%-16rem)]"
        headerExtras={
          selectedDeal && viewMode === 'view' ? (
            <DealPanelHeader
              viewMode={viewMode}
              selectedDeal={selectedDeal}
              submitting={submitting}
              creatingProject={creatingProject}
              setCreatingProject={setCreatingProject}
              setPanelError={setPanelError}
              setSelectedDeal={setSelectedDeal}
              handleStageTransition={handleStageTransition}
              navigate={navigate}
            />
          ) : undefined
        }
      >
        {selectedDeal && viewMode === 'view' && (
          <DealViewPanel
            selectedDeal={selectedDeal}
            panelError={panelError}
            submitting={submitting}
            customerContacts={customerContacts}
            loadingContacts={loadingContacts}
            handleStageTransition={handleStageTransition}
            openLightbox={openLightbox}
          />
        )}

        {selectedDeal && viewMode === 'edit' && (
          <DealEditForm
            selectedDeal={selectedDeal}
            customers={customers}
            panelError={panelError}
            submitting={submitting}
            handleDealChange={handleDealChange}
            setSelectedDeal={setSelectedDeal}
            setViewMode={setViewMode}
            handleUpdateDeal={handleUpdateDeal}
          />
        )}
      </RightPanel>

      {/* Lightbox */}
      {lightbox && createPortal(
        <div
          className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button
            aria-label="Close"
            className="absolute top-4 right-4 p-2 rounded-md bg-white/80 hover:bg-white shadow z-10"
            onClick={() => setLightbox(null)}
          >
            <X className="w-5 h-5 text-gray-800" />
          </button>
          {lightbox.type === 'image' && (
            <img
              src={lightbox.url}
              alt="preview"
              className="max-h-[90vh] max-w-[90vw] rounded-lg border border-white/20"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {lightbox.type === 'pdf' && (
            <iframe
              src={lightbox.url}
              title="PDF preview"
              className="w-[90vw] h-[90vh] rounded-lg bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {lightbox.type === 'video' && (
            <video
              src={lightbox.url}
              controls
              autoPlay
              className="w-[90vw] max-h-[90vh] rounded-lg bg-black"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {lightbox.type === 'doc' && (
            <iframe
              src={lightbox.url}
              title="Document preview"
              className="w-[90vw] h-[90vh] rounded-lg bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DealsPage;
